import { Box, Collapse, Divider, List, ListItem, Skeleton, Tabs, Typography, capitalize } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';

import { EventDetails, FrontFacingWellness, InCabinWellnessV1 } from '../../../backendsdk';
import { TrackedListItemButton as ListItemButton, TrackedTab as Tab } from '../../../components/TrackedComponents';
import useProfile from '../../../hooks/profile';
import palette from '../../ColorPalette';
import { WELLNESS_TYPE_MAP } from '../Device/WellnessBox';
import { EVENT_TYPE_MAP } from '../Event/Defs';
import { STATUS } from './DriverModal';

interface DriverModalSidebarProps {
    tab: STATUS;
    setTab: CallableFunction;
    sortedEvents: Array<Array<EventDetails>>;
    accidentEventIds: number[];
    selectedEvent: number | undefined;
    setSelectedEvent: CallableFunction;
    eventImages: Record<number, string>;
    isLoadingEvents: boolean;
    getItemType?: CallableFunction;
    listHeight: number;
}

const DriverModalSidebar: React.FC<DriverModalSidebarProps> = (props: DriverModalSidebarProps) => {
    const { t } = useTranslation();
    const placeholders = [
        t('content.safety.pending_placeholder'),
        t('content.safety.saved_placeholder'),
        t('content.safety.dismissed_placeholder'),
    ];

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        props.setTab(newValue);
    };

    const a11yProps = (index: STATUS) => {
        return {
            id: `tab-${index}`,
            'aria-controls': `tabpanel-${index}`,
        };
    };

    return (
        <>
            <Tabs variant="fullWidth" value={props.tab} onChange={handleChange} indicatorColor="secondary">
                <Tab
                    label={t('content.safety.pending_tab')}
                    {...a11yProps(STATUS.PENDING)}
                    id="pending-tag"
                    data-testid={`tab-0`}
                    wrapped
                />
                <Tab
                    label={t('content.safety.saved_tab')}
                    {...a11yProps(STATUS.SAVED)}
                    id="saved-tag"
                    data-testid={`tab-1`}
                    wrapped
                />
                <Tab
                    label={t('content.safety.dismissed_tab')}
                    {...a11yProps(STATUS.DISMISSED)}
                    id="dismissed-tag"
                    data-testid={`tab-2`}
                    wrapped
                />
            </Tabs>
            <Box sx={{ height: props.listHeight, overflowY: 'scroll' }}>
                {props.sortedEvents.map((events, idx) => {
                    return (
                        <TabPanel
                            key={idx}
                            value={props.tab}
                            index={idx}
                            events={events}
                            accidentEventIds={props.accidentEventIds}
                            selectedEvent={props.selectedEvent}
                            setSelectedEvent={props.setSelectedEvent}
                            eventImages={props.eventImages}
                            isLoading={props.isLoadingEvents}
                            placeholder={placeholders[idx]}
                            getItemType={props.getItemType}
                        />
                    );
                })}
            </Box>
        </>
    );
};

interface TabPanelProps {
    index: number;
    value: STATUS;
    events: Array<EventDetails>;
    accidentEventIds: number[];
    selectedEvent: number | undefined;
    setSelectedEvent: CallableFunction;
    eventImages: Record<string, string>;
    isLoading: boolean;
    placeholder: string;
    getItemType?: CallableFunction;
}

const TabPanel: React.FC<TabPanelProps> = (props: TabPanelProps) => {
    const { t } = useTranslation();
    const { profile } = useProfile();
    const isCurrent = props.value === props.index;

    const isAccident = (event: EventDetails) => props.accidentEventIds.includes(event.event_id);

    const renderEvent = (e: EventDetails) => {
        return (
            <>
                {props.eventImages[`event:${e.event_id}`] !== undefined ? (
                    <Box sx={{ width: '180px', height: '67.5px', mr: 1, flexShrink: 0 }}>
                        <img src={props.eventImages[`event:${e.event_id}`]} width="100%" />
                    </Box>
                ) : (
                    <Skeleton variant="rectangular" animation="wave" width={180} height={67.5} sx={{ mr: 1 }} />
                )}
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '67.5px' }}
                >
                    <Typography id={`${e.event_id}-type`} variant="body3" sx={{ fontWeight: 'bold' }}>
                        {t(isAccident(e) ? 'content.safety.accident' : EVENT_TYPE_MAP[e.type])}
                    </Typography>
                    <Typography id={`${e.event_id}-license-plate`} variant="body3">
                        {`${t('table.headers.license_plate')}: `}
                        {e.device.license_plate}
                    </Typography>
                    <Typography id={`${e.event_id}-risk`} variant="body3">
                        {t('table.headers.severity')}: {(isAccident(e) ? 100 : e.severity).toFixed(2)}
                    </Typography>
                    <Typography id={`${e.event_id}-datetime`} variant="body3">
                        {dayjs.unix(e.timestamp).format(profile.dateTimeFormat)}
                    </Typography>
                </Box>
            </>
        );
    };

    const renderWellnessStatus = (s: InCabinWellnessV1 | FrontFacingWellness) => {
        return (
            <>
                {!!props.getItemType && props.eventImages[`${props.getItemType(s)}:${s.id}`] !== undefined ? (
                    <Box sx={{ width: '180px', height: '67.5px', mr: 1, flexShrink: 0 }}>
                        <img src={props.eventImages[`${props.getItemType(s)}:${s.id}`]} width="100%" />
                    </Box>
                ) : (
                    <Skeleton variant="rectangular" animation="wave" width={180} height={67.5} sx={{ mr: 1 }} />
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {!!props.getItemType && (
                        <Typography variant="body3">{t(`content.wellness.${props.getItemType(s)}`)}</Typography>
                    )}
                    <Typography variant="body3" sx={{ fontWeight: 'bold' }}>
                        {capitalize(t(WELLNESS_TYPE_MAP[s.wellness_state as keyof typeof WELLNESS_TYPE_MAP]))}
                    </Typography>
                    <Typography variant="body3">{dayjs.unix(s.timestamp).format(profile.dateFormat)}</Typography>
                </Box>
            </>
        );
    };

    return (
        <div
            role="tabpanel"
            hidden={props.value !== props.index}
            id={`tabpanel-${props.index}`}
            aria-labelledby={`tab-${props.index}`}
        >
            {isCurrent ? (
                <Box sx={{ p: 0 }}>
                    <List disablePadding>
                        <TransitionGroup>
                            {props.events.map((e) => (
                                <Collapse key={e.event_id}>
                                    <ListItem
                                        sx={
                                            props.selectedEvent === e.event_id
                                                ? { backgroundColor: palette.white, p: 0 }
                                                : { p: 0 }
                                        }
                                    >
                                        <ListItemButton
                                            id={`${e.event_id}-event-btn`}
                                            data-testid={`${e.event_id}-event-btn`}
                                            aria-selected={props.selectedEvent === e.event_id}
                                            sx={{ p: 1 }}
                                            onClick={() => {
                                                if (props.selectedEvent !== e.event_id) {
                                                    props.setSelectedEvent(e.event_id);
                                                }
                                            }}
                                        >
                                            {'event_id' in e ? renderEvent(e) : renderWellnessStatus(e)}
                                        </ListItemButton>
                                    </ListItem>
                                    <Divider />
                                </Collapse>
                            ))}
                        </TransitionGroup>
                    </List>
                    {props.events.length === 0 ? (
                        <Box textAlign="center" sx={{ pt: 2 }}>
                            <Typography variant="overline" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                                {props.isLoading ? t('content.safety.loading') : props.placeholder}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
            ) : null}
        </div>
    );
};

export default DriverModalSidebar;
